import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  Package, 
  Truck, 
  CheckCircle2, 
  Clock, 
  MapPin,
  Phone,
  Loader2,
  AlertCircle,
  ArrowLeft,
  ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice } from '../lib/utils';

export default function TrackOrderPage() {
  const [phone, setPhone] = useState('');
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      processing: 'bg-purple-100 text-purple-800 border-purple-200',
      shipped: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      cod: 'bg-orange-100 text-orange-800 border-orange-200',
      paid: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'shipped':
        return <Truck className="w-5 h-5" />;
      case 'processing':
      case 'confirmed':
        return <Package className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    
    if (!phone.trim() || phone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    
    if (!orderId.trim()) {
      setError('Please enter your order ID');
      return;
    }
    
    setLoading(true);
    setError('');
    setOrder(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/orders/track`, {
        phone: phone.replace(/\D/g, ''),
        order_id: orderId.trim()
      });
      
      setOrder(response.data);
      toast.success('Order found!');
    } catch (err) {
      console.error('Track order error:', err);
      if (err.response?.status === 404) {
        setError('Order not found. Please check your phone number and order ID.');
      } else {
        setError('Failed to track order. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-green-700 hover:text-green-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Package className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Track Your Order</h1>
          <p className="text-gray-600">Enter your phone number and order ID to check status</p>
        </div>

        {/* Track Form */}
        <Card className="border-2 border-green-100 shadow-xl mb-8">
          <CardContent className="p-6">
            <form onSubmit={handleTrackOrder} className="space-y-4">
              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex">
                  <div className="flex items-center px-4 bg-gray-100 border-2 border-r-0 border-gray-200 rounded-l-lg">
                    <Phone className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-gray-600 font-medium">+91</span>
                  </div>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                      setError('');
                    }}
                    className="flex-1 border-2 border-gray-200 rounded-l-none focus:border-green-500 text-lg"
                    placeholder="Enter phone number"
                    maxLength={10}
                    data-testid="track-phone-input"
                  />
                </div>
              </div>

              {/* Order ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order ID
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    value={orderId}
                    onChange={(e) => {
                      setOrderId(e.target.value);
                      setError('');
                    }}
                    className="pl-10 border-2 border-gray-200 focus:border-green-500 text-lg"
                    placeholder="Enter your order ID"
                    data-testid="track-order-id-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  You can find your order ID in the confirmation message or email
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-6 text-lg"
                disabled={loading}
                data-testid="track-order-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Tracking...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Track Order
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Order Details */}
        {order && (
          <Card className="border-2 border-green-200 shadow-xl overflow-hidden" data-testid="order-details">
            <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl">Order #{order.id?.slice(-8).toUpperCase()}</CardTitle>
                  <p className="text-green-100 text-sm mt-1">
                    Placed on {formatDate(order.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(order.order_status)}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 space-y-6">
              {/* Status Badges */}
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Order Status:</span>
                  <Badge className={`${getStatusColor(order.order_status)} border`}>
                    {order.order_status?.toUpperCase()}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Payment:</span>
                  <Badge className={`${getStatusColor(order.payment_status)} border`}>
                    {order.payment_status?.toUpperCase()}
                  </Badge>
                </div>
                {order.payment_method && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Method:</span>
                    <Badge variant="outline">{order.payment_method}</Badge>
                  </div>
                )}
              </div>

              {/* Order Progress */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4">Order Progress</h4>
                <div className="flex items-center justify-between">
                  {['Confirmed', 'Processing', 'Shipped', 'Delivered'].map((step, idx) => {
                    const currentIdx = ['confirmed', 'processing', 'shipped', 'delivered'].indexOf(order.order_status?.toLowerCase());
                    const isActive = idx <= currentIdx;
                    const isCurrent = idx === currentIdx;
                    
                    return (
                      <div key={step} className="flex flex-col items-center flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive 
                            ? 'bg-green-600 text-white' 
                            : 'bg-gray-200 text-gray-400'
                        } ${isCurrent ? 'ring-4 ring-green-200' : ''}`}>
                          {idx === 0 && <Package className="w-5 h-5" />}
                          {idx === 1 && <Clock className="w-5 h-5" />}
                          {idx === 2 && <Truck className="w-5 h-5" />}
                          {idx === 3 && <CheckCircle2 className="w-5 h-5" />}
                        </div>
                        <span className={`text-xs mt-2 ${isActive ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                          {step}
                        </span>
                        {idx < 3 && (
                          <div className={`hidden sm:block absolute h-1 w-full ${isActive ? 'bg-green-600' : 'bg-gray-200'}`} 
                               style={{ top: '20px', left: '50%', width: 'calc(100% - 40px)' }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-green-600" />
                  Order Items
                </h4>
                <div className="space-y-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      {item.image && (
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900">{item.name}</h5>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-700">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Delivery Address */}
              {order.shipping_address && (
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Delivery Address</h4>
                    <p className="text-gray-600 text-sm">
                      {order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.postal_code}
                    </p>
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatPrice(order.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span>{order.shipping_cost > 0 ? formatPrice(order.shipping_cost) : 'Free'}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-green-700">{formatPrice(order.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            Need help? Contact us at{' '}
            <a href="tel:+919876543210" className="text-green-700 font-medium hover:underline">
              +91 98765 43210
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
